import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Dialog,
  ErrorState,
  Field,
  LoadingState,
  useToast,
} from '@/components/ui';
import {
  type DispensingRx,
  useDispensaryActions,
} from '@/features/admin-v2/api';
import { useMe } from '@/features/auth/session-context';
import { useLabelPreview } from '@/features/dispensing-labels/api';
import { LABEL_LAYOUTS, layoutById } from '@/features/dispensing-labels/layout';
import { canPhysicallyPrint } from '@/features/dispensing-labels/printing';
import {
  REPRINT_REASON_MAX,
  type LabelSheetData,
} from '@/features/dispensing-labels/types';
import { isApiError } from '@/lib/api';
import { LabelPreview, LabelPrintRoot } from './LabelSheet';
import { useLeafletQrImages } from './use-leaflet-qr-images';
import styles from './LabelsDialog.module.css';

const LAYOUT_KEY = 'ommri.labelLayout';

/** The remembered label size (a per-browser convenience; the default is 70 x 40 mm). */
function storedLayoutId(): string | null {
  try {
    return window.localStorage.getItem(LAYOUT_KEY);
  } catch {
    return null;
  }
}

/**
 * Labels for a signed, fully scanned prescription (blueprint request 4).
 *
 * The preview is a read-only GET: opening, refreshing or retrying it records
 * nothing. Only Print labels (production only) calls the POST that records one
 * print request, once, and then prints that response. A reprint needs a reason.
 * Outside production, a dedicated QA prescription (the server says so with
 * `testRecord.available`) can record an audited TEST print instead: nothing is
 * printed. Nothing here is retried automatically.
 */
export function LabelsDialog({
  rx,
  onClose,
}: {
  rx: DispensingRx;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const me = useMe();
  const actions = useDispensaryActions();
  const preview = useLabelPreview(rx.prescriptionId);
  const [reason, setReason] = useState('');
  const [reasonAskedByApi, setReasonAskedByApi] = useState(false);
  const [printSheet, setPrintSheet] = useState<LabelSheetData | null>(null);
  const [printing, setPrinting] = useState(false);
  const [printed, setPrinted] = useState(false);
  const [testRecorded, setTestRecorded] = useState(false);
  const [fit, setFit] = useState<{ slacks: number[]; clipped: number }>({
    slacks: [],
    clipped: 0,
  });
  const [layoutId, setLayoutId] = useState(
    () => layoutById(storedLayoutId()).id,
  );
  const layout = layoutById(layoutId);
  const previewQr = useLeafletQrImages(preview.data ?? null);
  const printQr = useLeafletQrImages(printSheet);

  const production = canPhysicallyPrint(me.appEnv);
  const needsReason = rx.labelsPrinted > 0 || reasonAskedByApi;
  const trimmed = reason.trim();
  const reasonValid =
    !needsReason ||
    (trimmed.length > 0 && trimmed.length <= REPRINT_REASON_MAX);
  const sheet = preview.data;
  const testRecordOffered =
    !production && sheet?.testRecord?.available === true;
  const recording =
    actions.labels.isPending || actions.testRecordLabels.isPending;
  const error = testRecordOffered
    ? actions.testRecordLabels.error
    : actions.labels.error;
  const reasonRefused = isApiError(error) && error.code === 'REPRINT_REASON';
  const externalUse = sheet?.labels.some((label) => label.externalUse) ?? false;
  const busy = recording || printing;
  // A label that still clips after splitting must not be printed or recorded.
  const fits = fit.clipped === 0;

  const requestPrint = () => {
    if (!production || !sheet || !reasonValid || busy || printed || !fits)
      return;
    actions.labels.mutate(
      { id: rx.prescriptionId, ...(needsReason ? { reason: trimmed } : {}) },
      {
        onSuccess: (data) => {
          setPrintSheet(data);
          setPrinting(true);
        },
        onError: (err) => {
          // The server wants a reason: ask for it. Nothing is retried here.
          if (isApiError(err) && err.code === 'REPRINT_REASON')
            setReasonAskedByApi(true);
        },
      },
    );
  };

  const requestTestRecord = () => {
    if (!testRecordOffered || !reasonValid || busy || testRecorded || !fits)
      return;
    actions.testRecordLabels.mutate(
      { id: rx.prescriptionId, ...(needsReason ? { reason: trimmed } : {}) },
      {
        onSuccess: () => {
          setTestRecorded(true);
          toast.show({
            tone: 'info',
            title: t('dispensingLabels.testRecorded'),
          });
        },
        onError: (err) => {
          if (isApiError(err) && err.code === 'REPRINT_REASON')
            setReasonAskedByApi(true);
        },
      },
    );
  };

  const chooseLayout = (id: string) => {
    setLayoutId(id);
    try {
      window.localStorage.setItem(LAYOUT_KEY, id);
    } catch {
      // A remembered size is a convenience only.
    }
  };

  const onPrinted = useCallback(() => {
    setPrinting(false);
    setPrinted(true);
    toast.show({ tone: 'info', title: t('dispensingLabels.printed') });
  }, [t, toast]);

  return (
    <>
      <Dialog
        open
        wide
        title={t('dispensingLabels.dialogTitle', { number: rx.number })}
        onClose={onClose}
        dismissible={!busy}
        footer={
          <>
            <Button onClick={onClose} disabled={busy}>
              {t('dispensingLabels.close')}
            </Button>
            {sheet && testRecordOffered && (
              <Button
                variant="primary"
                disabled={
                  !previewQr.ready || !reasonValid || testRecorded || busy
                }
                loading={recording}
                onClick={requestTestRecord}
              >
                {t('dispensingLabels.recordTest')}
              </Button>
            )}
            {sheet && (
              <Button
                variant="primary"
                disabled={
                  !production ||
                  !fits ||
                  !previewQr.ready ||
                  !reasonValid ||
                  printed ||
                  busy
                }
                loading={recording}
                onClick={requestPrint}
              >
                {t('dispensingLabels.print')}
              </Button>
            )}
          </>
        }
      >
        <div className={styles.body}>
          {preview.isLoading ? (
            <LoadingState label={t('dispensingLabels.previewLoading')} />
          ) : preview.error || !sheet ? (
            <ErrorState
              error={preview.error}
              onRetry={() => void preview.refetch()}
            />
          ) : (
            <>
              <h3 className={styles.heading}>
                {t('dispensingLabels.previewTitle')}
              </h3>
              {production ? (
                <p className={styles.note}>
                  {t('dispensingLabels.previewIntro')}{' '}
                  {t('dispensingLabels.printIntro')}
                </p>
              ) : (
                <p className={styles.notice} role="status">
                  {testRecordOffered
                    ? t('dispensingLabels.stagingNoticeTest')
                    : t('dispensingLabels.stagingNotice')}
                </p>
              )}
              {externalUse && (
                <p className={styles.notice} role="status">
                  {production
                    ? t('dispensingLabels.externalUseLanguages')
                    : t('dispensingLabels.externalUseTestPlaceholders')}
                </p>
              )}
              <Field
                label={t('dispensingLabels.layoutLabel')}
                hint={t('dispensingLabels.layoutHint')}
              >
                <select
                  className={styles.select}
                  value={layout.id}
                  onChange={(event) => chooseLayout(event.target.value)}
                  disabled={busy}
                >
                  {LABEL_LAYOUTS.map((option) => (
                    <option key={option.id} value={option.id}>
                      {t('dispensingLabels.layoutOption', {
                        width: option.widthMm,
                        height: option.heightMm,
                      })}
                    </option>
                  ))}
                </select>
              </Field>
              {sheet.labels.some((label) => label.leafletQrUrl === null) && (
                <p className={styles.note}>
                  {t('dispensingLabels.noLeafletQr')}
                </p>
              )}
              {(production || testRecordOffered) && needsReason && (
                <>
                  <p className={styles.note}>
                    {t('dispensingLabels.reprintIntro', {
                      count: Math.max(rx.labelsPrinted, 1),
                    })}
                  </p>
                  <Field
                    label={t('dispensingLabels.reasonLabel')}
                    hint={t('dispensingLabels.reasonHint', {
                      count: reason.length,
                      max: REPRINT_REASON_MAX,
                    })}
                    error={
                      reasonRefused
                        ? t('dispensingLabels.reasonRequiredByApi')
                        : undefined
                    }
                  >
                    <textarea
                      className={styles.textarea}
                      value={reason}
                      maxLength={REPRINT_REASON_MAX}
                      onChange={(event) => setReason(event.target.value)}
                    />
                  </Field>
                </>
              )}
              {!previewQr.ready ? (
                <LoadingState label={t('dispensingLabels.qrLoading')} />
              ) : (
                <LabelPreview
                  sheet={sheet}
                  images={previewQr.images}
                  marked={!production}
                  layout={layout}
                  onFit={setFit}
                />
              )}
              {error && !reasonRefused && (
                <ErrorState error={error} onRetry={null} />
              )}
              {printed && (
                <p className={styles.note}>{t('dispensingLabels.printed')}</p>
              )}
              {testRecorded && (
                <p className={styles.note}>
                  {t('dispensingLabels.testRecordedNote')}
                </p>
              )}
            </>
          )}
        </div>
      </Dialog>
      {production && printing && printSheet && printQr.ready && (
        <LabelPrintRoot
          sheet={printSheet}
          images={printQr.images}
          onDone={onPrinted}
          layout={layout}
          slacks={fit.slacks}
        />
      )}
    </>
  );
}
